import { Machine, assign, spawn } from 'xstate';
import _cloneDeep from 'lodash/cloneDeep';

import { sortByLatestDate, logDetails } from '@trqk/utils';

import {
    createStatementViewsMap,
    getSelectionLabel,
    getSelectionSets,
    getEmptyStatementSelectionContext,
    getInitialSelection,
} from './helpers';

const sortStatementViewByLatestDate = (
    a: TrqkStatementView,
    b: TrqkStatementView,
): number => sortByLatestDate(a?.end_date ?? null, b?.end_date ?? null);

/**
 * Handles statement selection for a list of statement views fetched for a given selection id, which
 * is a combination of a userId and entityId.
 */
export const statementSelectionMachine = Machine<
    StatementSelectionContext,
    // StatementSelectionSchema,
    StatementSelectionEvent
>(
    {
        type: 'compound',
        initial: 'loading',
        context: getEmptyStatementSelectionContext(),
        states: {
            loading: {
                on: {
                    GET_CONTEXT: {
                        // add 'logDetails' to an actions array to see the context and event
                        actions: [
                            'getStatementViewsMap',
                            'generateSelectionSets',
                            'setSelectedByPresetWithRegion',
                        ],
                        target: 'closed',
                    },
                },
            },
            closed: {
                on: {
                    GET_CONTEXT: {
                        actions: [
                            'getStatementViewsMap',
                            'generateSelectionSets',
                            'setSelectedByPresetWithRegion',
                        ],
                    },
                    ADD_STATEMENT_TO_SELECTIONS: {
                        actions: ['addStatementToSelections'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    DELETE_STATEMENT: {
                        actions: ['deleteStatement'],
                    },
                    START_EDITING: {
                        actions: ['startEditing'],
                        target: 'open',
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    SET_SELECTED_BY_PRESET_WITH_REGION: {
                        actions: ['setSelectedByPresetWithRegion'],
                    },
                    SET_SELECTED_BY_STATEMENT_ID_RANGE: {
                        actions: ['setSelectedByStatementIdRange'],
                    },
                },
            },
            open: {
                on: {
                    // We would not normally send the 'GET_CONTEXT' event when the statement
                    // selector is open, but it could happen.
                    GET_CONTEXT: {
                        actions: [
                            'getStatementViewsMap',
                            'generateSelectionSets',
                            'setSelectedByPresetWithRegion',
                        ],
                    },
                    ADD_STATEMENT_TO_SELECTIONS: {
                        actions: ['addStatementToSelections'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    DELETE_STATEMENT: {
                        actions: ['deleteStatement'],
                    },
                    SET_REGION_FILTER: {
                        actions: ['setRegionFilter'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    SET_SELECTED: {
                        actions: ['setSelected'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    TOGGLE_SELECTED: {
                        actions: ['toggleSelected'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    SET_SELECTED_BY_PRESET_BUTTON: {
                        target: 'closed',
                        actions: ['setSelectedByPresetButton'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    SAVE_EDITS: {
                        target: 'closed',
                        actions: ['saveEdits'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                    CANCEL_EDITING: {
                        target: 'closed',
                        actions: ['cancelEditing'],
                        cond: {
                            type: 'hasMatchingId',
                        },
                    },
                },
            },
        },
    },
    {
        actions: {
            logDetails,
            /**
             * Add a new statement, preserving the values of the `saved` and `editing`
             * StatementSelectorState.
             */
            addStatementToSelections: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'ADD_STATEMENT_TO_SELECTIONS') {
                        return context;
                    }

                    const {
                        value: { statementView },
                    } = event;

                    const prevStatementViews = Array.from(
                        context.statementViewsMap.values(),
                    );

                    const nextStatementViews = [
                        ...prevStatementViews,
                        statementView,
                    ]
                        .sort(sortStatementViewByLatestDate)
                        .filter(
                            ({ statement_id }: TrqkStatementView): boolean =>
                                statement_id !== null,
                        );

                    const statementViewsMap =
                        createStatementViewsMap(nextStatementViews);

                    const intermediateContext = {
                        ...context,
                        statementViewsMap,
                    };

                    // Preserve the current `saved` regionFilter and selectionSets
                    const savedRegionFilter = context.saved.regionFilter;
                    const savedSelectedIds =
                        context.saved.selectionSets[savedRegionFilter]
                            .selectedIds;

                    const initializers: SelectionSetInitializer[] = [
                        {
                            initialRegion: savedRegionFilter,
                            initialSelectedIds: savedSelectedIds,
                            statementSelectorState: 'saved',
                        },
                    ];

                    if (context?.editing) {
                        // Preserve the current `editing` regionFilter and selectionSets
                        const editingRegionFilter =
                            context?.editing?.regionFilter;
                        const editingSelectedIds =
                            context?.editing?.selectionSets?.[savedRegionFilter]
                                ?.selectedIds;

                        if (editingRegionFilter && editingSelectedIds) {
                            initializers.push({
                                initialRegion: editingRegionFilter,
                                initialSelectedIds: editingSelectedIds,
                                statementSelectorState: 'editing',
                            });
                        }
                    }

                    const nextContext = getSelectionSets({
                        context: intermediateContext,
                        initializers,
                    });

                    return nextContext;
                },
            ),
            /**
             * Delete a statement from the statementViewsMap and copy over old selectedIds to the
             * next `saved` context.
             */
            deleteStatement: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'DELETE_STATEMENT') {
                        return context;
                    }
                    const {
                        value: { statementId },
                    } = event;
                    const {
                        statementViewsMap,
                        editing,
                        saved,
                        onSelectStatements,
                    } = context;

                    // Remove statement that is being deleted from the statementViewsMap
                    statementViewsMap.delete(statementId);

                    const intermediateContext = {
                        ...context,
                        statementViewsMap,
                    };

                    const currentlySelectedIds =
                        saved.selectionSets[saved.regionFilter].selectedIds;

                    const deletedSelectedStatement =
                        currentlySelectedIds.includes(statementId);

                    const initializers: SelectionSetInitializer[] = [
                        {
                            // If the user deleted a statement that was selected, set the selected
                            // ids to an empty array (no selection)
                            initialSelectedIds: deletedSelectedStatement
                                ? []
                                : currentlySelectedIds,
                            initialRegion: saved.regionFilter,
                            statementSelectorState: 'saved',
                        },
                    ];

                    // Statement deletion should happen only when the statement selector is closed
                    // and `editing` is null. However, this block will ensure we handle deletion
                    // for an open statement selector if that does somehow occur.
                    if (editing !== null) {
                        initializers.push({
                            initialSelectedIds: editing.selectionSets[
                                editing.regionFilter
                            ].selectedIds.filter(
                                (priorSelectedId: string): boolean =>
                                    priorSelectedId !== statementId,
                            ),
                            initialRegion: editing.regionFilter,
                            statementSelectorState: 'editing',
                        });
                    }

                    // Re-initialize selection sets with updated data
                    const nextInitialContext = getSelectionSets({
                        context: intermediateContext,
                        initializers,
                    });

                    const { saved: nextSaved } = nextInitialContext;

                    if (onSelectStatements) {
                        const nextSelectedIds =
                            nextSaved.selectionSets[nextSaved.regionFilter]
                                .selectedIds;

                        onSelectStatements(nextSelectedIds);
                    }

                    return {
                        ...nextInitialContext,
                    };
                },
            ),
            /**
             * Convert statement views list into a `statementViewsMap` - a Map of statementViews
             * sorted by end_date with latest statements first
             */
            getStatementViewsMap: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'GET_CONTEXT') {
                        return context;
                    }

                    const {
                        id,
                        entityId,
                        pro,
                        shortLabels,
                        statementViews,
                        userId,
                    } = event.value;

                    const statementViewsSorted = [...statementViews]
                        .sort(sortStatementViewByLatestDate)
                        .filter(
                            ({ statement_id }: TrqkStatementView): boolean =>
                                statement_id !== null,
                        );

                    const statementViewsMap =
                        createStatementViewsMap(statementViewsSorted);

                    return {
                        ...context,
                        id,
                        entityId,
                        pro,
                        ready: false,
                        shortLabels,
                        statementViewsMap,
                        userId,
                    };
                },
            ),
            /**
             * Generate all values in `saved.selectionSets`
             */
            generateSelectionSets: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'GET_CONTEXT') {
                        return context;
                    }

                    const {
                        value: { onSelectStatements, urlParamSelectedIds },
                    } = event;
                    const { statementViewsMap } = context;

                    const { initialSelectedIds, initialRegion } =
                        getInitialSelection({
                            statementViewsMap,
                            urlParamSelectedIds,
                        });

                    const nextContext = getSelectionSets({
                        context,
                        initializers: [
                            {
                                initialSelectedIds,
                                initialRegion,
                                statementSelectorState: 'saved',
                            },
                        ],
                    });

                    if (onSelectStatements) {
                        onSelectStatements(initialSelectedIds);
                    }

                    return {
                        ...nextContext,
                        onSelectStatements,
                    };
                },
            ),

            /**
             * Clone selector state from `saved` to `editing`
             */
            startEditing: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'START_EDITING') {
                        return context;
                    }

                    const { saved } = context;
                    const editing = _cloneDeep(saved);

                    return {
                        ...context,
                        editing,
                    };
                },
            ),
            /**
             * Set the regionFilter on the `editing` context
             */
            setRegionFilter: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'SET_REGION_FILTER') {
                        return context;
                    }

                    if (context.editing === null) {
                        return context;
                    }

                    const {
                        value: { regionFilter },
                    } = event;
                    const {
                        editing: { selectionSets },
                    } = context;

                    return {
                        ...context,
                        editing: {
                            selectionSets,
                            regionFilter,
                        },
                    };
                },
            ),
            /**
             * Given a new list of selectedStatementIds, update `editing` context
             */
            setSelected: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'SET_SELECTED') {
                        return context;
                    }

                    const {
                        value: { selectedStatementIds },
                    } = event;
                    const { editing, statementViewsMap } = context;

                    if (editing === null) {
                        return context;
                    }

                    const { selectionSets, regionFilter } = editing;
                    const nextEditingSelectionSets = _cloneDeep(selectionSets);

                    const selectedViews = Array.from(
                        statementViewsMap.values(),
                    ).filter(
                        ({ statement_id }: TrqkStatementView): boolean =>
                            statement_id !== null &&
                            statement_id !== undefined &&
                            selectedStatementIds.includes(statement_id),
                    );

                    nextEditingSelectionSets[regionFilter].selectedIds =
                        selectedStatementIds;

                    nextEditingSelectionSets[regionFilter].label =
                        getSelectionLabel({
                            statementViews: selectedViews,
                            labelKey: regionFilter,
                            context,
                        });

                    return {
                        ...context,
                        editing: {
                            regionFilter,
                            selectionSets: nextEditingSelectionSets,
                        },
                    };
                },
            ),
            /**
             * Toggle a single statementId in the `editing` context on or off via a checkbox.
             * Enforces contiguous statement selection by automatically selecting / deselecting
             * statements based on the requested action.
             */
            toggleSelected: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'TOGGLE_SELECTED') {
                        return context;
                    }

                    const {
                        value: { statementId, selected },
                    } = event;
                    const { editing } = context;

                    if (editing === null) {
                        return context;
                    }

                    const { selectionSets, regionFilter } = editing;
                    const activeSelectionSet = selectionSets[regionFilter];
                    const { selectedIds, selectableViews } = activeSelectionSet;
                    const nextActiveSelectionSet = { ...activeSelectionSet };

                    if (selectedIds.length === 0 && selected) {
                        // If this is the first statement to be selected, set selectedIds to an
                        // array containing only the target statementId
                        nextActiveSelectionSet.selectedIds = [statementId];

                        const selectedView = selectableViews.find(
                            ({ statement_id }: TrqkStatementView): boolean =>
                                statement_id !== null &&
                                statement_id === statementId,
                        );

                        const statementViews =
                            selectedView === undefined ? [] : [selectedView];

                        nextActiveSelectionSet.label = getSelectionLabel({
                            statementViews,
                            labelKey: regionFilter,
                            context,
                        });

                        return {
                            ...context,
                            editing: {
                                regionFilter,
                                selectionSets: {
                                    ...selectionSets,
                                    [regionFilter]: nextActiveSelectionSet,
                                },
                            },
                        };
                    }

                    const indexOfTargetId = selectableViews.findIndex(
                        ({ statement_id }: TrqkStatementView): boolean =>
                            statement_id === statementId,
                    );

                    const indexOfFirstSelectedId = selectableViews.findIndex(
                        ({ statement_id }: TrqkStatementView): boolean =>
                            statement_id !== null &&
                            statement_id !== undefined &&
                            selectedIds.includes(statement_id),
                    );

                    if (indexOfTargetId < 0 || indexOfFirstSelectedId < 0) {
                        // It should not be possible to end up with either:
                        // 1. `indexOfTargetId < 0`, which indicates an `event.value.statementId`
                        // that is not included in the `selectableViews` list, or
                        // 2. `indexOfFirstSelectedId < 0`, which indicates the existence of an id
                        // in `selectedIds` that is not included in the `selectableViews` list.
                        // To be safe, we handle those cases by just returning the current context.
                        return context;
                    }

                    if (selected) {
                        // When user is trying to select a statement, select all IDs between the
                        // first currently selected ID and the target ID
                        const { nextSelectedIds, nextStatementViews } =
                            selectableViews.reduce(
                                (
                                    acc: {
                                        nextSelectedIds: string[];
                                        nextStatementViews: TrqkStatementView[];
                                    },
                                    curr: TrqkStatementView,
                                    index: number,
                                ): {
                                    nextSelectedIds: string[];
                                    nextStatementViews: TrqkStatementView[];
                                } => {
                                    if (
                                        curr.statement_id &&
                                        index >= indexOfFirstSelectedId &&
                                        index <= indexOfTargetId
                                    ) {
                                        acc.nextSelectedIds.push(
                                            curr.statement_id,
                                        );
                                        acc.nextStatementViews.push(curr);
                                    }

                                    return acc;
                                },
                                {
                                    nextSelectedIds: [],
                                    nextStatementViews: [],
                                },
                            );

                        nextActiveSelectionSet.selectedIds = nextSelectedIds;
                        nextActiveSelectionSet.label = getSelectionLabel({
                            statementViews: nextStatementViews,
                            labelKey: regionFilter,
                            context,
                        });

                        return {
                            ...context,
                            editing: {
                                regionFilter,
                                selectionSets: {
                                    ...selectionSets,
                                    [regionFilter]: nextActiveSelectionSet,
                                },
                            },
                        };
                    }

                    // When user is trying to de-select a single statement, force a de-select for
                    // all statements
                    nextActiveSelectionSet.selectedIds = [];
                    nextActiveSelectionSet.label = getSelectionLabel({
                        statementViews: [],
                        labelKey: regionFilter,
                        context,
                    });

                    return {
                        ...context,
                        editing: {
                            regionFilter,
                            selectionSets: {
                                ...selectionSets,
                                [regionFilter]: nextActiveSelectionSet,
                            },
                        },
                    };
                },
            ),
            /**
             * Set `saved[activeRegionFilter].selectedStatementIds` by a preset button value, i.e.
             * 'all' or 'yearToDate', and set `editing` to null. This action assumes the menu is in
             * the `open` state and uses the `event.value.activeRegionFilter` to set the
             * regionFilter.
             */
            setSelectedByPresetButton: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'SET_SELECTED_BY_PRESET_BUTTON') {
                        return context;
                    }
                    const { editing, onSelectStatements } = context;

                    if (editing === null) {
                        return context;
                    }

                    const { selectionSets } = editing;
                    const nextSelectionSets = _cloneDeep(selectionSets);
                    const {
                        value: { activeRegionFilter, selectionPreset },
                    } = event;

                    const nextSelectedIds =
                        nextSelectionSets[activeRegionFilter].presets[
                            selectionPreset
                        ].ids;

                    nextSelectionSets[activeRegionFilter].selectedIds =
                        nextSelectedIds;

                    if (onSelectStatements) {
                        onSelectStatements(nextSelectedIds);
                    }

                    nextSelectionSets[activeRegionFilter].label =
                        nextSelectionSets[activeRegionFilter].presets[
                            selectionPreset
                        ].label;

                    return {
                        ...context,
                        editing: null,
                        saved: {
                            regionFilter: activeRegionFilter,
                            selectionSets: nextSelectionSets,
                        },
                    };
                },
            ),
            /**
             * Set the statement selection from URL params. If presetsWithRegion.primary is provided
             * and corresponds to a preset with at least one statement ID, it will be used. If that
             * fails to happen, and presetsWithRegion.fallback is provided, and corresponds to a
             * preset with at least one statement ID, it will be used. If neither field results in
             * any selection, nothing happens (context is returned as is).
             *
             * This action is called both for the initial GET_CONTEXT and declaratively via the
             * SET_SELECTED_BY_PRESET_WITH_REGION event. See the {@link https://github.com/trqk/trqk-client/blob/develop/packages/web/src/components/pages/Insights/components/dataCards/TopFiveProgramsCard.tsx | TopFiveProgramsCard}
             * for an example of using SET_SELECTED_BY_PRESET_WITH_REGION.
             */
            setSelectedByPresetWithRegion: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (
                        event.type !== 'GET_CONTEXT' &&
                        event.type !== 'SET_SELECTED_BY_PRESET_WITH_REGION'
                    ) {
                        return context;
                    }

                    const { onSelectStatements } = context;

                    const {
                        value: { presetsWithRegion, urlParamSelectedIds },
                    } = event;

                    // If urlParamSelectedIds is present, the selectedIds have already been used
                    // to initialize the statement selection
                    if (urlParamSelectedIds && urlParamSelectedIds.length > 0) {
                        return context;
                    }

                    const {
                        saved: { selectionSets },
                    } = context;

                    const nextSelectionSets = _cloneDeep(selectionSets);

                    const presetsWithRegionArray: SelectionPresetWithRegion[] =
                        [];

                    if (presetsWithRegion?.primary) {
                        presetsWithRegionArray.push(
                            presetsWithRegion.primary as SelectionPresetWithRegion,
                        );
                    }

                    if (presetsWithRegion?.fallback) {
                        presetsWithRegionArray.push(
                            presetsWithRegion.fallback as SelectionPresetWithRegion,
                        );
                    }

                    if (presetsWithRegionArray.length === 0) {
                        return context;
                    }

                    let nextRegionFilter = 'standard' as PRORegionFilter;

                    for (const selectionPresetWithRegion of presetsWithRegionArray) {
                        let selectionPreset = 'all' as SelectionPreset;

                        switch (selectionPresetWithRegion) {
                            case 'all_domestic':
                                nextRegionFilter = 'standard';
                                selectionPreset = 'all';
                                break;
                            case 'last_two_domestic':
                                nextRegionFilter = 'standard';
                                selectionPreset = 'lastTwo';
                                break;
                            case 'year_to_date_domestic':
                                nextRegionFilter = 'standard';
                                selectionPreset = 'yearToDate';
                                break;
                            case 'all_international':
                                nextRegionFilter = 'international';
                                selectionPreset = 'all';
                                break;
                            case 'last_two_international':
                                nextRegionFilter = 'international';
                                selectionPreset = 'lastTwo';
                                break;
                            case 'year_to_date_international':
                                nextRegionFilter = 'international';
                                selectionPreset = 'yearToDate';
                                break;
                            default:
                                break;
                        }

                        const nextSelectedIds =
                            nextSelectionSets[nextRegionFilter].presets[
                                selectionPreset
                            ].ids;

                        if (nextSelectedIds.length > 0) {
                            nextSelectionSets[nextRegionFilter].selectedIds =
                                nextSelectedIds;

                            nextSelectionSets[nextRegionFilter].label =
                                nextSelectionSets[nextRegionFilter].presets[
                                    selectionPreset
                                ].label;

                            if (onSelectStatements) {
                                onSelectStatements(nextSelectedIds);
                            }

                            // This loop will try presetsWithRegion.primary first, and if it finds
                            // some IDs to select, it will use them and break there - if not, it
                            // will then try presetsWithRegion.fallback
                            break;
                        }
                    }

                    return {
                        ...context,
                        ready: true,
                        editing: null,
                        saved: {
                            regionFilter: nextRegionFilter,
                            selectionSets: nextSelectionSets,
                        },
                    };
                },
            ),
            /**
             *
             */
            setSelectedByStatementIdRange: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'SET_SELECTED_BY_STATEMENT_ID_RANGE') {
                        return context;
                    }

                    const {
                        value: { statementIdEnd, statementIdStart },
                    } = event;
                    const { statementViewsMap } = context;

                    const urlParamSelectedIds = [];

                    if (statementIdEnd) {
                        urlParamSelectedIds.push(statementIdEnd);
                    }

                    if (statementIdStart) {
                        urlParamSelectedIds.push(statementIdStart);
                    }

                    const { initialSelectedIds, initialRegion } =
                        getInitialSelection({
                            statementViewsMap,
                            urlParamSelectedIds,
                        });

                    const nextContext = getSelectionSets({
                        context,
                        initializers: [
                            {
                                initialSelectedIds,
                                initialRegion,
                                statementSelectorState: 'saved',
                            },
                        ],
                    });

                    return nextContext;
                },
            ),
            /**
             * Clone selector state from `editing` to `saved` and set `editing` to null
             */
            saveEdits: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'SAVE_EDITS') {
                        return context;
                    }
                    const { editing, onSelectStatements } = context;

                    if (editing === null) {
                        return context;
                    }

                    const saved = _cloneDeep(editing);

                    if (onSelectStatements) {
                        const activeRegionFilter = saved.regionFilter;

                        const nextSelectedIds =
                            saved.selectionSets[activeRegionFilter].selectedIds;

                        onSelectStatements(nextSelectedIds);
                    }

                    return {
                        ...context,
                        editing: null,
                        saved,
                    };
                },
            ),
            /**
             * Set `editing` to null
             */
            cancelEditing: assign(
                (
                    context: StatementSelectionContext,
                    event: StatementSelectionEvent,
                ) => {
                    if (event.type !== 'CANCEL_EDITING') {
                        return context;
                    }

                    return {
                        ...context,
                        editing: null,
                    };
                },
            ),
        },
        guards: {
            /**
             * Ensure this machine only reacts to events that target it specifically. Each machine
             * is a child that receives all events from the parent, so some of the events may be
             * intended for a different child.
             * This guard is not used for the DELETE_STATEMENT event, because although a given
             * statementId will only exist in one context, we do not have access to the entityId
             * when deleting a statement from the ManageStatementsModal. The entityId is used in
             * conjunction with the userId as the id for the statementSelectionMachine context, so
             * so we have to let the DELETE_STATEMENT event flow up to all statementSelectionMachine
             * contexts.
             */
            hasMatchingId: (
                context: StatementSelectionContext,
                event: StatementSelectionMetaEvent,
            ): boolean => event.value.id === context.id,
        },
    },
);

export const statementSelectionManagerMachine = Machine<
    StatementSelectionManagerContext,
    // StatementSelectionManagerStateSchema,
    StatementSelectionMetaEvent
>(
    {
        type: 'compound',
        initial: 'running',
        id: 'statementSelectionManagerMachine',
        context: {
            selectionMachines: {},
            selectionId: '',
        },
        states: {
            running: {
                on: {
                    ADD_STATEMENT: {
                        actions: ['addStatement'],
                    },
                    DELETE_SELECTION_MACHINE: {
                        actions: ['deleteSelectionMachine'],
                    },
                    USE_SELECTION_MACHINE: {
                        actions: ['useSelectionMachine'],
                    },
                    CLEAR_CONTEXTS: {
                        actions: ['clearContexts'],
                    },
                },
            },
        },
    },
    {
        actions: {
            logDetails,
            /**
             * When a new statement is added, if it is the first statement for a given entity, then
             * spawn and initialize a new selection machine. If not, the event will be sent
             * to the existing selection machine that has the matching id.
             */
            addStatement: assign(
                (
                    context: StatementSelectionManagerContext,
                    event: StatementSelectionMetaEvent,
                ) => {
                    if (event.type !== 'ADD_STATEMENT') {
                        return context;
                    }

                    const { selectionMachines } = context;

                    const {
                        value: {
                            id,
                            userId,
                            entityId,
                            shortLabels,
                            statementView,
                        },
                    } = event;

                    let selectionMachine = selectionMachines?.[id];

                    if (selectionMachine === undefined) {
                        selectionMachine = spawn(statementSelectionMachine, {
                            name: id,
                            autoForward: true,
                        });

                        selectionMachine.send({
                            type: 'GET_CONTEXT',
                            value: {
                                id,
                                entityId,
                                pro: statementView?.pro ?? null,
                                shortLabels,
                                presetsWithRegion: null,
                                statementViews: [statementView],
                                userId,
                                urlParamSelectedIds: null,
                            },
                        });
                    } else {
                        selectionMachine.send({
                            type: 'ADD_STATEMENT_TO_SELECTIONS',
                            value: {
                                id,
                                entityId,
                                shortLabels,
                                statementView,
                                userId,
                            },
                        });
                    }

                    return context;
                },
            ),
            deleteSelectionMachine: assign(
                (
                    context: StatementSelectionManagerContext,
                    event: StatementSelectionMetaEvent,
                ) => {
                    if (event.type !== 'DELETE_SELECTION_MACHINE') {
                        return context;
                    }

                    const { selectionMachines } = context;
                    const {
                        value: { id },
                    } = event;

                    const selectionMachine:
                        | StatementSelectionMachine
                        | undefined = selectionMachines?.[id];

                    if (selectionMachine?.stop !== undefined) {
                        selectionMachine.stop();
                    }

                    const nextSelectionMachines = { ...selectionMachines };

                    delete nextSelectionMachines[id];

                    return {
                        ...context,
                        selectionMachines: nextSelectionMachines,
                    };
                },
            ),
            useSelectionMachine: assign(
                (
                    context: StatementSelectionManagerContext,
                    event: StatementSelectionMetaEvent,
                ) => {
                    if (event.type !== 'USE_SELECTION_MACHINE') {
                        return context;
                    }

                    const { selectionMachines } = context;

                    const {
                        value: {
                            id,
                            entityId,
                            onSelectStatements,
                            pro,
                            presetsWithRegion,
                            shortLabels,
                            statementViews,
                            userId,
                            urlParamSelectedIds,
                        },
                    } = event;

                    let selectionMachine = selectionMachines?.[id];

                    if (selectionMachine === undefined) {
                        selectionMachine = spawn(statementSelectionMachine, {
                            name: id,
                            autoForward: true,
                        });
                    }

                    selectionMachine.send({
                        type: 'GET_CONTEXT',
                        value: {
                            id,
                            entityId,
                            onSelectStatements,
                            pro,
                            presetsWithRegion,
                            shortLabels,
                            statementViews,
                            userId,
                            urlParamSelectedIds,
                        },
                    });

                    return {
                        ...context,
                        selectionId: id,
                        selectionMachines: {
                            ...selectionMachines,
                            [id]: selectionMachine,
                        },
                    };
                },
            ),
            /**
             * Stop and delete all selectionMachines when user signs out
             */
            clearContexts: assign(
                (
                    context: StatementSelectionManagerContext,
                    event: StatementSelectionMetaEvent,
                ) => {
                    if (event.type !== 'CLEAR_CONTEXTS') {
                        return context;
                    }

                    const { selectionMachines } = context;

                    for (const [key, selectionMachine] of Object.entries(
                        selectionMachines,
                    )) {
                        if (selectionMachine?.stop) {
                            selectionMachine.stop();
                        }

                        delete selectionMachines[key];
                    }

                    return context;
                },
            ),
        },
    },
);
